<script setup lang="ts">
import { ref, computed } from 'vue'
import { Send } from 'lucide-vue-next'

const props = defineProps<{
  disabled?: boolean
  sending?: boolean
}>()

const emit = defineEmits<{
  send: [body: string]
}>()

const body = ref('')
const maxLength = 2000

const charCount = computed(() => body.value.length)
const canSend = computed(() => body.value.trim().length > 0 && !props.sending && !props.disabled)

const handleSend = () => {
  if (!canSend.value) return
  emit('send', body.value.trim())
  body.value = ''
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    handleSend()
  }
}
</script>

<template>
  <div class="border-t border-gray-200 px-4 py-3">
    <div class="flex items-end gap-2">
      <div class="flex-1 relative">
        <textarea
          v-model="body"
          :maxlength="maxLength"
          :disabled="disabled"
          placeholder="Type a message..."
          rows="1"
          class="w-full resize-none rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          style="min-height: 40px; max-height: 120px;"
          @keydown="handleKeydown"
          @input="($event.target as HTMLTextAreaElement).style.height = 'auto'; ($event.target as HTMLTextAreaElement).style.height = ($event.target as HTMLTextAreaElement).scrollHeight + 'px'"
        />
        <span
          v-if="charCount > maxLength * 0.8"
          class="absolute bottom-1.5 right-2 text-[10px]"
          :class="charCount >= maxLength ? 'text-red-500' : 'text-gray-400'"
        >
          {{ charCount }}/{{ maxLength }}
        </span>
      </div>
      <button
        :disabled="!canSend"
        class="shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        @click="handleSend"
      >
        <Send v-if="!sending" class="w-4 h-4" />
        <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
      </button>
    </div>
  </div>
</template>
